import React from "react";
import UserScreenWindow from "../Components/UserScreenWindow";
import Select from "react-select";
import { useState } from "react";
import { storage } from "../../../utils/firebase/Firebase.utils";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { v4 } from "uuid";
import { setDocData } from "../../../utils/firebase/Firebase.utils";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  FaExclamationCircle,
  FaImage,
  FaVideo,
  FaUpload,
  FaMapMarkerAlt,
  FaInfoCircle,
  FaPaperPlane,
} from "react-icons/fa";
import { MdDescription } from "react-icons/md";
import { BiSolidHome } from "react-icons/bi";
import { IoWarning } from "react-icons/io5";
import { notifyConcernFiling } from "../../../utils/userActivityNotifier";
import { createConcernNotification } from "../../../utils/userNotificationHelper";
import { storeNotification } from "../../../services/notificationService";
import { dualNotification } from "../../../utils/EleghoUtils";
import FileUploadAndView from "../Components/FileUploadAndView";
import { useCompany } from "../../../context/CompanyContext";
import { adminEmailAddress } from "../../../utils/constants";

export default function ConcernsForm() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);
  const [concern, setConcern] = useState("");
  const [formData, setFormData] = useState({
    identifier: currentUser.linkedIdentifiers[0],
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeUpload, setActiveUpload] = useState("");
  const [assignedId, setAssignedId] = useState(v4().slice(0, 8).toUpperCase());
  const [files, setFiles] = useState({});

  const { getTerm, getCapitalizedTerm } = useCompany();

  const concernList = [
    "Electricity",
    "Water Supply",
    "Water Leaks",
    "Internet",
    "Violation",
    "Neighbors",
    "Cutting of Trees",
    "Defective Water Meter",
    "Others",
  ];

  const identifiers = currentUser.linkedIdentifiers.map((identifier) => ({
    value: identifier,
    label: identifier,
  }));

  const options = concernList.map((concern) => ({
    value: concern,
    label: concern,
  }));

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: "35px",
      border: "1px solid #e5e7eb",
      boxShadow: "none",
      borderRadius: "6px",
      "&:hover": {
        border: "1px solid #363062",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: "0px",
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.75rem",
      padding: "8px",
      backgroundColor: state.isSelected
        ? "#363062"
        : state.isFocused
        ? "#f3f4f6"
        : null,
      color: state.isSelected ? "white" : "#374151",
    }),
  };

  const handleSelectConcern = (selectedConcern) => {
    setConcern(selectedConcern.value);
  };

  const handleSelectLot = (selected) => {
    setFormData({ ...formData, identifier: selected.value });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleUpload = (e, fileName) => {
    const file = e.target.files[0];
    const storageRef = ref(
      storage,
      `concerns/${assignedId}/${file.name} + ${v4()}`
    );
    const uploadTask = uploadBytesResumable(storageRef, file);
    setActiveUpload(fileName);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(progress);
      },
      (error) => {},
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFiles({
            ...files,
            [fileName]: downloadURL,
          });
          setFormData({ ...formData, [fileName]: downloadURL });
          setActiveUpload("");
        });
      }
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    if (!concern) {
      toast.error("Please select a concern");
      setIsProcessing(false);
      return;
    }

    if (!formData.description) {
      toast.error("Please enter a description");
      setIsProcessing(false);
      return;
    }

    const concernData = {
      identifier: formData.identifier,
      concern: concern,
      description: formData.description,
      image: formData.image || "",
      video: formData.video || "",
      user: currentUser.email,
      createdBy: currentUser.displayName,
      uid: currentUser.uid,
      status: "Pending",
      createdAt: new Date().toLocaleString(),
      id: assignedId,
      email: currentUser.email,
    };

    await setDocData("concerns", assignedId, concernData);

    const notificationObject = {
      recipient: adminEmailAddress,
      recipientName: "Admin",
      sender: currentUser.email,
      senderName: currentUser.displayName,
      type: "concern",
      subject: "New Concern Filed",
      message: `A new concern has been filed by ${currentUser.displayName} of ${formData.identifier}.`,
      url: "/property-management?type=Concerns",
      id: assignedId,
    };

    await dualNotification(notificationObject);

    toast.success("Concern filed successfully");
    setIsProcessing(false);
    navigate(`/concern-confirmation/${assignedId}`);
  };

  return (
    <UserScreenWindow label="Concerns">
      <div className="w-full mx-auto px-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaExclamationCircle className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                File a Concern
              </h1>
              <p className="text-xs text-gray-500">
                Submit your concerns to the management
              </p>
              <p className="text-xs text-gray-500 mt-0.5">Ref: {assignedId}</p>
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mt-4">
          {/* Lot Number */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaMapMarkerAlt className="text-xs" />
              {getCapitalizedTerm("identifier")}
            </label>
            <Select
              options={identifiers}
              name="identifier"
              placeholder={`Select ${getCapitalizedTerm("identifier")}`}
              onChange={handleSelectLot}
              defaultValue={identifiers[0]}
              value={
                identifiers.find(
                  (option) => option.value === formData.identifier
                ) || identifiers[0]
              }
              styles={selectStyles}
              className="text-xs"
            />
          </div>

          {/* Concern Type */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <IoWarning className="text-xs" />
              Concern Type
            </label>
            <Select
              options={options}
              placeholder="Select Concern Type"
              onChange={handleSelectConcern}
              styles={selectStyles}
              className="text-xs"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-[#363062] flex items-center gap-1.5">
              <FaInfoCircle className="text-xs" />
              Description
            </label>
            <textarea
              className="w-full rounded-md bg-gray-50 border border-gray-200 p-2.5 text-xs focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
              rows={4}
              placeholder="Please provide details about your concern..."
              name="description"
              onChange={handleChange}
            />
          </div>

          {/* Media Upload */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              {/* Photo Upload */}
              <div className="bg-gray-50 rounded-md p-3">
                <FileUploadAndView
                  doc="image"
                  files={files}
                  handleUpload={handleUpload}
                  progress={progress}
                  activeUpload={activeUpload}
                />
              </div>

              {/* Video Upload */}
              <div className="bg-gray-50 rounded-md p-3">
                <FileUploadAndView
                  doc="video"
                  files={files}
                  handleUpload={handleUpload}
                  progress={progress}
                  activeUpload={activeUpload}
                />
              </div>
            </div>

            <div>
              {files.image && (
                <div className="space-y-2">
                  <div className="font-medium text-xs text-[#363062]">
                    Image Preview
                  </div>
                  <div className="relative h-64 w-full overflow-hidden rounded-lg border border-gray-200">
                    <img
                      src={files.image}
                      className="h-full w-full object-contain"
                      alt="Uploaded image"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="mt-8 mb-4">
          <div className="max-w-md mx-auto">
            <button
              onClick={handleSubmit}
              disabled={isProcessing || activeUpload}
              className={`w-full py-3 rounded-md text-xs font-medium shadow-lg flex items-center justify-center gap-2 transition-colors ${
                isProcessing || activeUpload
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-[#363062] hover:bg-[#2d2850] text-white"
              }`}
            >
              {isProcessing ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : activeUpload ? (
                <>
                  <svg
                    className="animate-spin h-3.5 w-3.5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Uploading Files... Please Wait
                </>
              ) : (
                <>
                  <FaPaperPlane className="text-xs" />
                  Submit Concern
                </>
              )}
            </button>
            {activeUpload && (
              <p className="text-xs text-gray-500 text-center mt-2">
                Please wait while {activeUpload} is being uploaded (
                {Math.round(progress)}%)
              </p>
            )}
          </div>
        </div>
      </div>
    </UserScreenWindow>
  );
}
